/**
 * VideoVariantsTabContent — Variants tab content for the Video Generator bottom panel.
 *
 * Contains: video history grid with selection, pending spinners, empty state.
 * Each cell renders at the aspect ratio of its source variant (read from
 * BasicAssetMetadata.aspect_ratio when present), falling back to the node's
 * default aspect ratio so the variant preview always matches its underlying.
 */

import { memo, useState, useRef, useEffect, useMemo, forwardRef, useImperativeHandle, useCallback, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { Play, Square } from 'lucide-react'
import { cn } from '../../../../lib/utils'
import { createNadDebug } from '@ponpon/api-client'
import { getVariantLabel, type VideoHistoryEntry } from './VideoHistoryPopover'
import { VideoPreviewPlayer, type VideoPreviewPlayerHandle } from './VideoPreviewPlayer'
import { ASPECT_RATIO_MAP } from './constants'

/**
 * Parse a CSS aspect-ratio string (e.g., "16/9") into a numeric value (width / height).
 * Returns 16/9 for invalid/missing input as a safe default.
 */
function parseAspectRatio(ar: string): number {
  const parts = ar.split('/')
  const w = Number(parts[0])
  const h = Number(parts[1])
  if (!w || !h || w <= 0 || h <= 0) return 16 / 9
  return w / h
}

/**
 * Normalize a stored aspect_ratio value into CSS aspect-ratio string form.
 * Accepts schema label form ("9:16") or already-CSS form ("9/16").
 * Returns null when the value is missing or unparseable so callers can
 * cleanly fall back to a node-level default.
 */
function toCssAspect(value: string | undefined | null): string | null {
  if (!value) return null
  // Schema label form: "9:16" -> "9/16" via the canonical map
  if (ASPECT_RATIO_MAP[value]) return ASPECT_RATIO_MAP[value]
  // CSS form already: "9/16"
  if (/^\d+\s*\/\s*\d+$/.test(value)) return value.replace(/\s/g, '')
  return null
}

const variantDebug = createNadDebug('dw:video-variant-aspect')
const nullHistoryDebug = createNadDebug('dw:video-variant-null-result:history')
// Module-level set to dedupe one-shot render traces across StrictMode double mounts.
const SEEN_RENDER_KEYS = new Set<string>()
const SEEN_NULL_HISTORY_KEYS = new Set<string>()

function getVideoVariantKey(
  entry: NonNullable<VideoHistoryEntry>,
  realIdx: number,
): string {
  const id = entry.result?.id
  const localPath = entry.result?.local_path
  if (id && localPath) return `${id}|${localPath}`
  return id || localPath || `idx-${realIdx}`
}

/**
 * Compute dynamic height for the Variants tab content area.
 * 1-2 variants -> 1 row; 3-4 -> 2 rows; 5+ -> 7 rows (with scroll).
 *
 * Each cell is rendered into a square footprint (column-width × column-width)
 * so portrait and landscape variants share the same grid bounding box; the
 * actual video shape is centered within. Row height therefore equals column
 * width regardless of variant aspect.
 *
 * Note: this function currently has no production caller — VideoGeneratorNode
 * hardcodes `contentHeight={520}` for the panel — so the grid scrolls inside
 * the existing 520px area. The helper is forward-work for dynamic-height
 * callers and is exercised by the sprint-2 unit tests.
 */
export function getVideoVariantsHeight(
  variantCount: number,
  nodeWidth: number = 400,
): number {
  if (variantCount === 0) return 150

  // Panel is calc(100% + 120px) wide; min 632px
  const panelWidth = Math.max(nodeWidth + 120, 632)
  // p-3 = 24px total horizontal padding; gap-2 = 8px; 2 columns
  const cellWidth = (panelWidth - 24 - 8) / 2
  // Square footprint — the cell is a column-width × column-width box.
  const cellHeight = cellWidth
  const gap = 8
  const padding = 24
  const playAllHeaderHeight = variantCount >= 2 ? 40 : 0

  const rows = Math.ceil(variantCount / 2)

  if (rows <= 2) {
    return Math.round(rows * cellHeight + (rows - 1) * gap + padding + playAllHeaderHeight)
  }

  // 5+ variants: cap at 7 rows with scroll
  return Math.round(7 * cellHeight + 6 * gap + padding + playAllHeaderHeight)
}

/**
 * Read the per-entry aspect ratio (CSS form) from a history entry's
 * BasicAssetMetadata, falling back to the node-level default.
 */
function getEntryAspect(entry: VideoHistoryEntry, defaultAspect: string): string {
  const raw = entry?.result?.metadata?.aspect_ratio
  const ar = typeof raw === 'string' ? raw : undefined
  return toCssAspect(ar) ?? defaultAspect
}

const VideoHistoryCell = memo(forwardRef<VideoPreviewPlayerHandle, {
  entry: NonNullable<VideoHistoryEntry>
  realIdx: number
  label: string
  isSelected: boolean
  /**
   * Per-entry aspect ratio of this video — informational only (used for the
   * NAD render trace). Cell rendering uses `gridAspect` so the whole grid
   * looks uniform.
   */
  cellAspect: string
  /**
   * The grid's base aspect ratio. ALL cells render at this aspect — for a
   * homogeneous grid it equals every entry's own aspect; for a mixed-aspect
   * grid it equals the latest variant's aspect (the user's "base" choice).
   * 16:9 source content in a 9:16 cell is cropped via the inner video's
   * `object-cover`, mirroring how the main preview crops to fit.
   */
  gridAspect: string
  defaultAspect: string
  nodeId?: string
  buildVideoUrl: (localPath: string) => string | null
  onSelect: (index: number) => void
  onExpand: (videoUrl: string) => void
  onDownload?: (localPath: string, realIdx: number) => void
  onEnded?: () => void
}>(function VideoHistoryCell(
  { entry, realIdx, label, isSelected, cellAspect, gridAspect, defaultAspect, nodeId, buildVideoUrl, onSelect, onExpand, onDownload, onEnded },
  ref,
) {
  const [isHovering, setIsHovering] = useState(false)
  const playerRef = useRef<VideoPreviewPlayerHandle>(null)
  const videoPath = entry.result?.local_path
  const videoUrl = videoPath ? buildVideoUrl(videoPath) : null
  const variantId = entry.result?.id || `idx-${realIdx}`
  const isPerEntry = cellAspect !== defaultAspect

  // Forward the internal playerRef to the parent via the forwarded ref
  useImperativeHandle(ref, () => playerRef.current!, [])

  // One-shot render trace (deduped by module-level set so StrictMode
  // double-mount does not double-emit). Includes both per-entry aspect
  // and the grid's chosen base aspect so a mixed-aspect grid is debuggable.
  useEffect(() => {
    const key = `${nodeId ?? 'unknown'}|${variantId}|${cellAspect}|${gridAspect}`
    if (SEEN_RENDER_KEYS.has(key)) return
    SEEN_RENDER_KEYS.add(key)
    variantDebug({
      event: 'video-variant:render',
      nodeId: nodeId ?? null,
      variantIndex: realIdx,
      variantId,
      entryAspectRatio: cellAspect,
      gridAspectRatio: gridAspect,
      source: isPerEntry ? 'per-entry' : 'node-level',
    })
  }, [nodeId, variantId, realIdx, cellAspect, gridAspect, isPerEntry])

  // Every cell renders at the grid's base aspect (uniform shape across the
  // grid). Width fills the grid track; height derives from aspect-ratio.
  // Per-entry source aspect doesn't change the cell shape — VideoPreviewPlayer's
  // object-cover handles the source/cell aspect mismatch by cropping (matching
  // the main preview's behavior).
  const outerStyle: CSSProperties = {
    aspectRatio: gridAspect,
    width: '100%',
  }
  const innerStyle: CSSProperties = {
    aspectRatio: gridAspect,
    width: '100%',
    height: '100%',
  }

  return (
    <div
      data-testid={`video-variant-item-${realIdx}`}
      className={cn(
        'relative rounded-lg overflow-hidden border-2 transition-all cursor-pointer group',
        isSelected
          ? 'border-primary ring-2 ring-primary/30 p-0.5'
          : 'border-transparent hover:border-border',
      )}
      style={outerStyle}
      onClick={() => onSelect(realIdx)}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div
        data-testid={`video-variant-aspect-box-${realIdx}`}
        className="rounded-md overflow-hidden"
        style={innerStyle}
      >
        {videoUrl ? (
          <VideoPreviewPlayer
            ref={playerRef}
            videoUrl={videoUrl}
            isHovering={isHovering}
            showExpand={true}
            showDownload={true}
            onEnded={onEnded}
            onExpand={() => {
              if (videoUrl) onExpand(videoUrl)
            }}
            onDownload={() => {
              if (videoPath && onDownload) {
                onDownload(videoPath, realIdx)
              }
            }}
          />
        ) : (
          <div className="w-full h-full bg-surface-2 flex items-center justify-center">
            <svg
              className="w-4 h-4 text-text-muted/30"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}
      </div>
      <div className="absolute bottom-1.5 left-1.5 text-xxs text-white bg-black/60 px-1 rounded z-20">
        {label}
      </div>
    </div>
  )
}))

VideoHistoryCell.displayName = 'VideoHistoryCell'

export interface VideoVariantsTabContentProps {
  history: VideoHistoryEntry[]
  pickedIndex: number
  onSelectVariant: (index: number) => void
  batchPending: number
  buildVideoUrl: (localPath: string) => string | null
  onDownloadVariant?: (localPath: string, realIdx: number) => void
  /**
   * CSS aspect-ratio (e.g. "9/16") applied when an entry lacks its own
   * persisted `aspect_ratio` metadata. Required so the variant grid never
   * silently falls back to a hardcoded 16/9.
   */
  defaultAspectRatio: string
  /** Used to scope NAD render traces; optional in tests. */
  nodeId?: string
}

export const VideoVariantsTabContent = memo(function VideoVariantsTabContent({
  history,
  pickedIndex,
  onSelectVariant,
  batchPending,
  buildVideoUrl,
  onDownloadVariant,
  defaultAspectRatio,
  nodeId,
}: VideoVariantsTabContentProps) {
  const visibleHistoryEntries = useMemo(
    () => history
      .map((entry, realIdx) => ({ entry, realIdx }))
      .filter(
        (item): item is { entry: NonNullable<VideoHistoryEntry>; realIdx: number } =>
          item.entry?.result != null,
      ),
    [history],
  )
  const invalidHistoryCount = history.length - visibleHistoryEntries.length
  const variantCount = visibleHistoryEntries.length + batchPending

  const handleSelectVariant = useCallback((index: number) => {
    onSelectVariant(index)
  }, [onSelectVariant])

  useEffect(() => {
    if (invalidHistoryCount === 0) return
    const key = `${nodeId ?? 'unknown'}|${history.length}|${invalidHistoryCount}|${visibleHistoryEntries.length}`
    if (SEEN_NULL_HISTORY_KEYS.has(key)) return
    SEEN_NULL_HISTORY_KEYS.add(key)
    nullHistoryDebug({
      event: 'video-variant-history:null-slots-skipped',
      trace_id: `video-variant-null-result:${nodeId ?? 'unknown'}`,
      node_id: nodeId ?? null,
      original_length: history.length,
      failed_count: invalidHistoryCount,
      remaining_count: visibleHistoryEntries.length,
    })
  }, [history.length, invalidHistoryCount, nodeId, visibleHistoryEntries.length])

  // Base aspect for the grid:
  //   - Latest variant's aspect when history is non-empty (chronological
  //     last entry — the "current intent" the user just generated).
  //   - The node's defaultAspectRatio when history is empty (e.g. only
  //     pending placeholders).
  // Every cell renders at this aspect; column count adapts to it so a
  // portrait grid shows 3 columns of compact cells instead of 2 columns
  // of cells that scroll forever.
  const latestVisibleEntry = visibleHistoryEntries[visibleHistoryEntries.length - 1]?.entry
  const gridAspect =
    latestVisibleEntry
      ? getEntryAspect(latestVisibleEntry, defaultAspectRatio)
      : defaultAspectRatio
  // Portrait base → 3 columns (more cells per row offsets the height
  // each cell takes); landscape/square base → 2 columns (cells already
  // wide and short, fewer columns reads better).
  const columnCount = parseAspectRatio(gridAspect) < 1 ? 3 : 2

  // Fullscreen lightbox state
  const [fullscreenVideoUrl, setFullscreenVideoUrl] = useState<string | null>(null)
  const handleExpand = useCallback((url: string) => {
    setFullscreenVideoUrl(url)
  }, [])

  // Play All state
  const playerRefs = useRef<(VideoPreviewPlayerHandle | null)[]>([])
  const [isPlayingAll, setIsPlayingAll] = useState(false)
  const finishedCountRef = useRef(0)
  const totalPlayableRef = useRef(0)

  const handlePlayAll = useCallback(() => {
    const refs = playerRefs.current
    let playable = 0
    finishedCountRef.current = 0

    for (const handle of refs) {
      if (!handle?.videoElement) continue
      const video = handle.videoElement
      video.currentTime = 0
      video.muted = true
      video.play().catch(() => {})
      playable++
    }

    if (playable > 0) {
      totalPlayableRef.current = playable
      setIsPlayingAll(true)
    }
  }, [])

  const handleStopAll = useCallback(() => {
    for (const handle of playerRefs.current) {
      if (!handle?.videoElement) continue
      handle.videoElement.pause()
    }
    setIsPlayingAll(false)
    finishedCountRef.current = 0
  }, [])

  const handleVideoEnded = useCallback(() => {
    finishedCountRef.current++
    if (finishedCountRef.current >= totalPlayableRef.current) {
      setIsPlayingAll(false)
      finishedCountRef.current = 0
    }
  }, [])

  // Keep refs array in sync with rendered history length.
  playerRefs.current.length = visibleHistoryEntries.length

  return (
    <>
    <div className="h-full p-3 overflow-y-auto nowheel">
      {variantCount === 0 ? (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <svg
            className="w-8 h-8 text-text-muted/30 mb-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          <p className="text-xs text-text-muted">
            No videos generated yet
          </p>
          <p className="text-xxs text-text-muted/60 mt-1">
            Generate to create variants
          </p>
        </div>
      ) : (
        <>
          {/* Play All header */}
          {visibleHistoryEntries.length >= 2 && (
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-text-secondary font-medium">
                {visibleHistoryEntries.length} variant{visibleHistoryEntries.length !== 1 ? 's' : ''}
              </span>
              <button
                onClick={isPlayingAll ? handleStopAll : handlePlayAll}
                className={cn(
                  'flex items-center gap-1 px-2 py-1 rounded text-xs transition-colors',
                  isPlayingAll
                    ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                    : 'bg-white/10 text-text-secondary hover:bg-white/20 hover:text-foreground'
                )}
                data-testid="play-all-button"
              >
                {isPlayingAll ? (
                  <>
                    <Square size={12} fill="currentColor" />
                    <span>Stop</span>
                  </>
                ) : (
                  <>
                    <Play size={12} fill="currentColor" />
                    <span>Play All</span>
                  </>
                )}
              </button>
            </div>
          )}
          <div
            className="grid gap-2"
            style={{
              gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
            }}
          >
            {/* Pending spinners — render at the grid's base aspect so they
                line up with the rendered cells (uniform shape across the
                grid). */}
            {Array.from({ length: batchPending }).map(
              (_, i) => {
                return (
                  <div
                    key={`pending-${i}`}
                    data-testid={`video-variant-pending-${i}`}
                    className="rounded-lg bg-surface-2 flex items-center justify-center border border-border-subtle"
                    style={{
                      aspectRatio: gridAspect,
                      width: '100%',
                    }}
                  >
                    <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
                  </div>
                )
              }
            )}
            {/* History items (newest first) */}
            {[...visibleHistoryEntries]
              .reverse()
              .map(({ entry, realIdx }, displayIdx) => {
                const cellAspect = getEntryAspect(entry, defaultAspectRatio)
                const variantKey = getVideoVariantKey(entry, realIdx)
                return (
                  <VideoHistoryCell
                    key={variantKey}
                    ref={(handle) => {
                      playerRefs.current[displayIdx] = handle
                    }}
                    entry={entry}
                    realIdx={realIdx}
                    label={getVariantLabel(history, realIdx)}
                    isSelected={realIdx === pickedIndex}
                    cellAspect={cellAspect}
                    gridAspect={gridAspect}
                    defaultAspect={defaultAspectRatio}
                    nodeId={nodeId}
                    buildVideoUrl={buildVideoUrl}
                    onSelect={handleSelectVariant}
                    onExpand={handleExpand}
                    onDownload={onDownloadVariant}
                    onEnded={handleVideoEnded}
                  />
                )
              })}
          </div>
        </>
      )}
    </div>

    {/* Fullscreen Video Modal */}
    {fullscreenVideoUrl &&
      createPortal(
        <div
          data-testid="video-fullscreen-modal"
          className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-8"
          onClick={() => setFullscreenVideoUrl(null)}
        >
          <div
            className="relative w-full max-w-6xl max-h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="video-fullscreen-close"
              onClick={() => setFullscreenVideoUrl(null)}
              className="absolute -top-12 right-0 text-text-secondary hover:text-foreground transition-colors"
            >
              <svg
                className="w-8 h-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <video
              src={fullscreenVideoUrl}
              controls
              autoPlay
              className="max-w-full max-h-[85vh] rounded-lg shadow-2xl bg-black"
            />
          </div>
        </div>,
        document.body
      )}
    </>
  )
})

VideoVariantsTabContent.displayName = 'VideoVariantsTabContent'
